import React from 'react';
import { motion } from 'framer-motion';
import tw from 'twin.macro';
import styled from 'styled-components';
import { css } from 'styled-components/macro'; //eslint-disable-line

import useAnimatedNavToggler from 'shared/ui-kit/helpers/useAnimatedNavToggler.js';

import { ReactComponent as MenuIcon } from 'feather-icons/dist/icons/menu.svg';
import { ReactComponent as CloseIcon } from 'feather-icons/dist/icons/x.svg';
import { ConnectWalletButton } from '../../entities/wallet';
import { Select } from '../../shared/ui-kit/components/misc/Select';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Root = tw.header`
  flex justify-between items-center
  max-w-screen-xl mx-auto
`;

export const NavLinks = tw.div`inline-flex items-center gap-[2rem]`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = tw(Link)`
  text-lg my-2 lg:mx-6 lg:my-0
  font-black tracking-wide transition duration-300 hocus:text-primary-500
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex font-black border-b-0 text-2xl! ml-0! my-0!`};

  img {
    ${tw`min-w-[45px] w-[45px] h-[60px] object-contain lg:w-[100px] lg:h-[140px]`}
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 justify-between items-center`;
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
export const MobileNavLinks = motion(styled.div`
  ${tw`lg:hidden z-10 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg text-gray-900 bg-white`}
  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:grid grid-cols-3 items-center w-full
`;

export const Header = ({ roundedHeaderButton = false, logoLink, links, className, collapseBreakpointClass = 'lg' }) => {
  const { i18n, t } = useTranslation();
  /*
   * This header component accepts an optionals "links" prop that specifies the links to render in the navbar.
   * This links props should be an array of "NavLinks" components which is exported from this file.
   * Each "NavLinks" component can contain any amount of "NavLink" component, also exported from this file.
   * This allows this Header to be multi column.
   * So If you pass only a single item in the array with only one NavLinks component as root, you will get 2 column header.
   * Left part will be LogoLink, and the right part will be the the NavLinks component you
   * supplied.
   * Similarly if you pass 2 items in the links array, then you will get 3 columns, the left will be "LogoLink", the center will be the first "NavLinks" component in the array and the right will be the second "NavLinks" component in the links array.
   * You can also choose to directly modify the links here by not passing any links from the parent component and
   * changing the defaultLinks variable below below.
   * If you manipulate links here, all the styling on the links is already done for you. If you pass links yourself though, you are responsible for styling the links or use the helper styled components that are defined here (NavLink)
   */
  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink to="/faq">FAQ</NavLink>
      {/*<NavLink href="/#">Blog</NavLink>*/}
      {/*<NavLink href="/#">Pricing</NavLink>*/}
      {/*<NavLink href="/#">Contact Us</NavLink>*/}
      {/*<NavLink href="/#" tw="lg:ml-12!">*/}
      {/*  Login*/}
      {/*</NavLink>*/}
      <Select
        value={i18n.language}
        onChange={({ target }) => {
          i18n.changeLanguage(target?.value);
        }}
      >
        <Select.Option value="en">
          <div css={tw`flex items-center gap-2`}>
            <img src="https://media.flaticon.com/dist/min/img/flags/en.svg" css={tw`w-[1em] h-[1em]`} />
            En
          </div>
        </Select.Option>
        <Select.Option value="fr">
          <div css={tw`flex items-center gap-2`}>
            <img src="https://media.flaticon.com/dist/min/img/flags/fr.svg" css={tw`w-[1em] h-[1em]`} />
            Fr
          </div>
        </Select.Option>
        <Select.Option value="es">
          <div css={tw`flex items-center gap-2`}>
            <img src="https://media.flaticon.com/dist/min/img/flags/es.svg" css={tw`w-[1em] h-[1em]`} />
            Es
          </div>
        </Select.Option>
      </Select>
      <ConnectWalletButton />
    </NavLinks>
  ];

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink to="/">
      <img src="/images/agedor_logo.png" alt="logo" />
    </LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Root className={className || 'header-light'}>
      <DesktopNavLinks>
        {logoLink}
        <div className="justify-self-center">
          <div
            className="title"
            style={{
              fontWeight: 400,
              textAlign: 'center',
              lineHeight: 1.3,
              fontSize: 20,
              maxWidth: 500
            }}
          >
            {t('baseHeading')}
          </div>
        </div>
        <div className="justify-self-end">
            {links}
        </div>
      </DesktopNavLinks>

      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <div>
          <div
            className="title"
            style={{
              fontWeight: 400,
              textAlign: 'center',
              lineHeight: 1.3,
              fontSize: 20,
              maxWidth: 500
            }}
          >
            {t('baseHeading')}
          </div>
        </div>
        <MobileNavLinks
          initial={{ x: '150%', display: 'none' }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          {links}
        </MobileNavLinks>
        <div className="z-20">
          <NavToggle onClick={toggleNavbar}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
          </NavToggle>
        </div>
      </MobileNavLinksContainer>
    </Root>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden gap-4`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`
  }
};
